import { createContext, useContext } from 'react'
import type { ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react'
import { EmptyState } from './EmptyState'
import { Skeleton } from './Skeleton'
import { cx } from './cx'

/**
 * Shared data-table primitive (UID-229). Consolidates the ~11 hand-rolled
 * `<table>` blocks that had each grown their own class family (`data-table`,
 * `parts-table`, `cart-line-table`, `recent-orders-table`,
 * `rfq-review-parts-table`) — each drifting in padding / sort affordance /
 * keyboard + screen-reader behavior. Extracting the semantic structure,
 * sort affordance, and empty/loading states here means they are specified,
 * a11y-tested, and token-styled ONCE.
 *
 * This is a COMPOSITIONAL primitive (`Table` + `Table.Head`/`Body`/`Row`/
 * `HeaderCell`/`Cell` + `Table.Empty`/`LoadingRows`), not a column-config
 * data grid: the existing tables render richly bespoke cells (links, chips,
 * ⋮ menus, thumbnails), so callers keep authoring their own cell content and
 * this primitive owns the wrapper, semantics, and shared behavior. Migration
 * is therefore a near-mechanical markup swap that preserves every pixel.
 *
 * Visual parity: the root emits the existing `.data-table` /
 * `.data-table-wrapper` classes (styled in global.css) SO migrated tables
 * render identically, plus a `.ui-table` hook that carries the incremental
 * primitive-only styling (sort affordance, optional zebra striping) in
 * ui.css. `--color-surface-stripe` / `--shadow-inset-bottom` (tokens.css)
 * are the design's own table stripe (`--bg-2`) and row-separator
 * (`--shadow-inset-bottom`) values.
 */

/** Column count made available to `Table.Empty` / `Table.LoadingRows` so they
 * derive their colSpan / column count from a single source instead of the
 * caller hand-maintaining a number on each (UID-229 review #10). `undefined`
 * means the `<Table columnCount>` prop wasn't set; consumers then fall back to
 * their own explicit prop. */
const TableColumnCountContext = createContext<number | undefined>(undefined)

type CellAlign = 'start' | 'center' | 'end'

function alignClass(align: CellAlign | undefined): string | undefined {
  if (!align || align === 'start') return undefined
  return align === 'center' ? 'ui-table-cell-center' : 'ui-table-cell-end'
}

export interface TableProps {
  /** Table caption — REQUIRED for a screen-reader-navigable data table
   * (accessibility-wcag). Rendered visually hidden by default; set
   * `captionVisible` to show it. */
  readonly caption: string
  readonly captionVisible?: boolean
  /** Zebra-stripe alternate body rows using the design's `--bg-2` stripe. */
  readonly striped?: boolean
  /** Sets `aria-busy` on the table while data is loading (e.g. when the body
   * is `<Table.LoadingRows>`), so assistive tech knows the region's contents
   * are changing. Pair with an accessible loading status where appropriate. */
  readonly busy?: boolean
  /** Total column count. When set, `Table.Empty` / `Table.LoadingRows` derive
   * their colSpan / column count from it, so a single number governs the table
   * and can't desync between the empty and loading states (review #10). */
  readonly columnCount?: number
  readonly className?: string
  /** The composed `<Table.Head>` + `<Table.Body>`. */
  readonly children: ReactNode
}

export function Table({
  caption,
  captionVisible = false,
  striped = false,
  busy = false,
  columnCount,
  className,
  children,
}: TableProps) {
  return (
    <TableColumnCountContext.Provider value={columnCount}>
      <div className="ui-table-wrapper data-table-wrapper">
        <table
          className={cx('ui-table', 'data-table', striped && 'ui-table-striped', className)}
          aria-busy={busy || undefined}
        >
          <caption className={captionVisible ? 'ui-table-caption' : 'sr-only'}>{caption}</caption>
          {children}
        </table>
      </div>
    </TableColumnCountContext.Provider>
  )
}

export function TableHead({ children }: { readonly children: ReactNode }) {
  return <thead>{children}</thead>
}

export function TableBody({ children }: { readonly children: ReactNode }) {
  return <tbody>{children}</tbody>
}

export interface TableRowProps {
  readonly className?: string
  readonly children: ReactNode
}

export function TableRow({ className, children }: TableRowProps) {
  return <tr className={className}>{children}</tr>
}

/** Sort state for a sortable column header. `direction` drives both the
 * visual indicator and the `<th aria-sort>` value; `onSort` is called when
 * the header's toggle button is activated (click or keyboard). */
export interface TableSort {
  readonly direction: 'asc' | 'desc' | 'none'
  readonly onSort: () => void
}

export interface TableHeaderCellProps
  extends Omit<ThHTMLAttributes<HTMLTableCellElement>, 'scope' | 'aria-sort' | 'align'> {
  readonly align?: CellAlign
  /** When present, renders the header label inside a toggle `<button>` and
   * sets `aria-sort` on the `<th>` — the accessible sortable-column pattern.
   * Omit for a plain, non-sortable header. */
  readonly sort?: TableSort
  readonly children: ReactNode
}

const ARIA_SORT: Record<TableSort['direction'], 'ascending' | 'descending' | 'none'> = {
  asc: 'ascending',
  desc: 'descending',
  none: 'none',
}

export function TableHeaderCell({
  align,
  sort,
  className,
  children,
  ...rest
}: TableHeaderCellProps) {
  const classes = cx('ui-table-th', alignClass(align), className)
  if (!sort) {
    return (
      <th scope="col" className={classes} {...rest}>
        {children}
      </th>
    )
  }
  return (
    <th scope="col" className={classes} aria-sort={ARIA_SORT[sort.direction]} {...rest}>
      <button type="button" className="ui-table-sort" onClick={sort.onSort}>
        {children}
        {/* Purpose spoken to screen readers (state itself comes from the
         * `<th aria-sort>` above). The visible label remains the start of the
         * accessible name (WCAG 2.5.3 Label in Name); the arrow is decorative. */}
        <span className="sr-only"> — sortable column</span>
        <span className="ui-table-sort-indicator" aria-hidden="true">
          {sort.direction === 'asc' ? '↑' : sort.direction === 'desc' ? '↓' : '↕'}
        </span>
      </button>
    </th>
  )
}

export interface TableCellProps extends Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> {
  readonly align?: CellAlign
  readonly children?: ReactNode
}

export function TableCell({ align, className, children, ...rest }: TableCellProps) {
  return (
    <td className={cx(alignClass(align), className)} {...rest}>
      {children}
    </td>
  )
}

export interface TableEmptyProps {
  /** Column count the placeholder spans. Optional when the parent
   * `<Table columnCount>` is set — it's then derived from context so it can't
   * desync from the header (review #10). Pass explicitly otherwise. */
  readonly colSpan?: number
  readonly title: string
  readonly detail?: string
}

/** Full-width body row rendering the shared EmptyState — the one place an
 * empty data table's zero-result copy lives. */
export function TableEmpty({ colSpan, title, detail }: TableEmptyProps) {
  const columnCount = useContext(TableColumnCountContext)
  if (import.meta.env.DEV && colSpan === undefined && columnCount === undefined) {
    console.warn(
      'Table.Empty: no `colSpan` and no `<Table columnCount>` — the empty cell spans only 1 column. Pass one so it spans the full table.',
    )
  }
  const span = colSpan ?? columnCount ?? 1
  return (
    <tr>
      <td colSpan={span} className="ui-table-empty-cell">
        <EmptyState title={title} {...(detail === undefined ? {} : { detail })} />
      </td>
    </tr>
  )
}

export interface TableLoadingRowsProps {
  /** Number of skeleton rows to render. */
  readonly rows: number
  /** Number of columns — one Skeleton cell per column. Optional when the
   * parent `<Table columnCount>` is set (derived from context, review #10);
   * pass explicitly otherwise. */
  readonly columns?: number
}

/** Skeleton body rows for the loading state. Each `<Skeleton>` is itself
 * `aria-hidden` (it is purely decorative — see Skeleton.tsx), matching the
 * OrderTable skeleton pattern, so the placeholder shimmer is not announced;
 * pair with an accessible loading status where the caller manages
 * focus/announcements. */
export function TableLoadingRows({ rows, columns }: TableLoadingRowsProps) {
  const columnCount = useContext(TableColumnCountContext)
  if (import.meta.env.DEV && columns === undefined && columnCount === undefined) {
    console.warn(
      'Table.LoadingRows: no `columns` and no `<Table columnCount>` — rendering 1 skeleton column. Pass one to match the table width.',
    )
  }
  const cols = columns ?? columnCount ?? 1
  return (
    <>
      {Array.from({ length: rows }, (_, rowIndex) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length static skeleton placeholders, never reordered
        <tr key={`ui-table-skeleton-row-${rowIndex}`}>
          {Array.from({ length: cols }, (_, colIndex) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length static skeleton placeholders, never reordered
            <td key={`ui-table-skeleton-cell-${rowIndex}-${colIndex}`}>
              <Skeleton width="70%" />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

Table.Head = TableHead
Table.Body = TableBody
Table.Row = TableRow
Table.HeaderCell = TableHeaderCell
Table.Cell = TableCell
Table.Empty = TableEmpty
Table.LoadingRows = TableLoadingRows
